import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

/**
 * Pre-configured Axios instance for all API calls.
 *
 * - Attaches the Cognito access token as a Bearer header on every request.
 * - On 401, attempts a silent token refresh; if that fails, signs out and
 *   redirects to the Cognito hosted UI.
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach JWT ──────────────────────────────────────────
apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // No valid session — request will proceed without auth header
  }
  return config;
});

// ── Response interceptor: handle 401 → silent refresh → redirect ─────────────
let isRefreshing = false;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Force a token refresh
          const session = await fetchAuthSession({ forceRefresh: true });
          const newToken = session.tokens?.idToken?.toString();
          isRefreshing = false;

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch {
          isRefreshing = false;
        }

        // Refresh failed — sign out and redirect to login
        await signOut();
        window.location.href = '/';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
